import { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBalanceInfo, editBalanceDetails } from '../../../../../store/slices/Tuition/tuitionAdminSlice';
import { balanceFields } from '../../../../../config/balanceFieldsConfig';
import lockIcon from '../../../../../../public/assets/images/lockSolid.svg';
import ErrorIcon from '../../../../../../public/assets/images/errorCircleSmall.svg';
import { safeGet } from '../../../../../utils/safeGet';
import Notifications from '../../../../../components/common/Notifications';
import Loader from '../../../../../components/common/Loader';
import TuitionErrorBanner from '../../../../../components/common/TuitionErrorBanner';


interface BalancesProps {
  applicationData?: any;
  applicationId?: string;
}

// Configuration for balance field groups
const balanceGroupsConfig = [
  {
    id: 'eligibleAmount',
    label: 'Eligible amount',
    currentField: 'currentYearBalance',
    priorField: 'previousYearBalance',
    editable: false,
  },
  {
    id: 'eligibleOverride',
    label: 'Eligible amount override',
    currentField: 'currentYearEligibleOverride',
    priorField: 'priorYearEligibleOverride',
    editable: false,
  },
  {
    id: 'outstandingFees',
    label: 'Outstanding estimated fees',
    currentField: 'currentYearOutstanding',
    priorField: 'previousYearOutstanding',
    editable: false,
  },
  {
    id: 'actualFees',
    label: 'Actual fees',
    currentField: 'currentYearActual',
    priorField: 'previousYearActual',
    editable: false,
  },
  {
    id: 'reimbursableFees',
    label: 'Reimbursable fees',
    currentField: 'currentYearReimbursable',
    priorField: 'previousYearReimbursable',
    editable: false,
  },
];

const balanceRemainingConfig = [
  {
    id: 'eligibleRemaining',
    label: 'Eligible amount remaining',
    currentField: 'currentYearEligibleRemaining',
    priorField: 'priorYearEligibleRemaining',
    editable: false,
  },
];

const formatCurrencyDisplay = (value: number) => {
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const parseBalanceNumber = (value: any) => {
  if (value === undefined || value === null || value === '') return 0;
  const s = String(value).replaceAll(/[^0-9.-]+/g, '');
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const Balances: FC<BalancesProps> = ({ applicationData, applicationId }) => {
  const dispatch = useDispatch();
  const { balances, balancesLoading, balancesError, balancesPatchError } = useSelector((state: any) => state.tuitionAdmin);
  const [balanceData, setBalanceData] = useState<Record<string, string>>({});
  const [originalBalanceData, setOriginalBalanceData] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [showPatchErrorBanner, setShowPatchErrorBanner] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showValidation, setShowValidation] = useState(false);

  // Show the patch error banner locally and auto-hide after 5 seconds
  useEffect(() => {
    if (balancesPatchError) {
      setShowPatchErrorBanner(true);
      const timer = setTimeout(() => setShowPatchErrorBanner(false), 5000);
      return () => clearTimeout(timer);
    }
    setShowPatchErrorBanner(false);
  }, [balancesPatchError]);

  // Reset edit mode when a new row/view is selected
  useEffect(() => {
    setIsEditMode(false);
    setFieldErrors({});
    setShowValidation(false);
  }, [applicationId, applicationData]);

  const getOverrideCap = (): number | null => {
    const rawCap = balances?.maxEligibleAmount;
    if (rawCap === undefined || rawCap === null || rawCap === '') {
      return null;
    }

    const cap = parseBalanceNumber(rawCap);
    return Number.isFinite(cap) && cap > 0 ? cap : null;
  };

  const setEligibilityErrors = (
    setCurrReimbursable: boolean,
    setPriorReimbursable: boolean,
    setCurrCap: boolean,
    currCapValue?: number
  ) => {
    const currMessages: string[] = [];
    const priorMessages: string[] = [];

    if (setCurrReimbursable) {
      currMessages.push('Current Eligible Amount Override is less than the Reimbursable Amount.');
    }
    if (setCurrCap) {
      currMessages.push(`Current Eligible Amount Override cannot exceed the maximum allowed amount of ${formatCurrencyDisplay(currCapValue ?? 0)}.`);
    }
    if (setPriorReimbursable) {
      priorMessages.push('Previous Eligible Amount Override is less than the Reimbursable Amount.');
    }
    const errors: Record<string, string> = {};
    if (currMessages.length) errors.currentYearEligibleOverride = currMessages.join(' ');
    if (priorMessages.length) errors.priorYearEligibleOverride = priorMessages.join(' ');
    errors.eligibility = [errors.priorYearEligibleOverride, errors.currentYearEligibleOverride].filter(Boolean).join(' ');
    setFieldErrors(prev => ({ ...prev, ...errors }));
    setShowValidation(true);
  };

  const handleOverrideFieldChange = (fieldName: string, value: string) => {
    // Keep the input editable; enforce the current-year cap when Save is clicked.
    setBalanceData(prev => ({ ...prev, [fieldName]: value }));
    setFieldErrors(prev => ({ ...prev, [fieldName]: '' }));
    setShowValidation(false);
  };

  // Fetch balance data from API
  useEffect(() => {
    if (applicationId) {
      const nuid = safeGet(applicationData?.employeeNuId, '');
 
      if (!nuid) {
        // missing nuid; proceed without explicit logging
      }
      dispatch(getBalanceInfo({ applicationId, nuid }) as any);
    }
  }, [applicationId, applicationData?.employeeNuId, dispatch]);

  // Sync state with API response data
  useEffect(() => {
    if (balances) {
      const newBalanceData: Record<string, string> = {};
      
      // Map API response fields to our field names
      const apiToLocalFieldMapping: Record<string, string> = {
        shiftStdHours: 'standardHours',
        cyEligibleAmount: 'currentYearBalance',
        pyEligibleAmount: 'previousYearBalance',
        cyEligibleAmountOverride: 'currentYearEligibleOverride',
        pyEligibleAmountOverride: 'priorYearEligibleOverride',
        cyOutstandingBalance: 'currentYearOutstanding',
        pyOutstandingBalance: 'previousYearOutstanding',
        cyActualBalance: 'currentYearActual',
        pyActualBalance: 'previousYearActual',
        cyReimbursableBalance: 'currentYearReimbursable',
        pyReimbursableBalance: 'previousYearReimbursable',
        cyRemainingBalance: 'currentYearEligibleRemaining',
        pyRemainingBalance: 'priorYearEligibleRemaining',
      };

      const formatTwo = (v: any) => {
        if (v === undefined || v === null || v === '') return '';
        const n = Number(v);
        return Number.isNaN(n) ? String(v) : n.toFixed(2);
      };

      balanceFields.forEach((field) => {
        // Find the API field name for this local field
        const apiFieldName = Object.keys(apiToLocalFieldMapping).find(
          key => apiToLocalFieldMapping[key] === field.name
        );
        
        const rawValue = apiFieldName ? balances?.[apiFieldName] : balances?.[field.name];
        // Format eligible remaining fields to 2 decimal places to avoid floating point artifacts
        if (field.name === 'currentYearEligibleRemaining' || field.name === 'priorYearEligibleRemaining') {
          newBalanceData[field.name] = formatTwo(rawValue);
        } else {
          newBalanceData[field.name] = rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
        }
      });

      setBalanceData(newBalanceData);
      setOriginalBalanceData(newBalanceData);
    }
  }, [balances]);

  // Save/edit handler

  const renderGroupedFields = (
    groupLabel: string,
    currentYearFieldName: string,
    priorYearFieldName: string,
    canEditCurrent: boolean = false,
    canEditPrior: boolean = false
  ) => {
    const currentYearField = balanceFields.find(f => f.name === currentYearFieldName);
    const priorYearField = balanceFields.find(f => f.name === priorYearFieldName);
    
    if (!currentYearField || !priorYearField) return null;

    return (
      <div className="!p-[16px]">
        <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
          <div className="flex items-center gap-[37px]">
            <div>
              <label className="label-ds2 text-[18px] font-[350] mb-0 !w-[210px] flex-shrink-0 text-right">
                {groupLabel}
              </label>
            </div>
            {/* Current year field */}
            <div>
              <div className="helper-text font-semibold text-sm" id={`${currentYearFieldName}-helper`}>
                {currentYearField.helperText}
              </div>
              <div className="relative">
                <div className="input-with-icon">
                  <input
                    autoComplete="on"
                        className={`!w-[547px] h-[48px] flex-shrink-0 block ds-form__control ${
                          canEditCurrent && !isSaving ? '' : 'ds-form__disabled-field bg-gray-100 text-gray-600 pr-12'
                        }`}
                        disabled={!canEditCurrent || isSaving}
                    id={currentYearFieldName}
                    name={currentYearFieldName}
                    type="text"
                    value={balanceData[currentYearFieldName] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                     if (currentYearFieldName === 'currentYearEligibleOverride') {
                       handleOverrideFieldChange(currentYearFieldName, val);
                     } else {
                       setBalanceData(prev => ({ ...prev, [currentYearFieldName]: val }));
                       // Clear inline error for this field and hide validation until Save is clicked
                       setFieldErrors(prev => ({ ...prev, [currentYearFieldName]: '' }));
                       setShowValidation(false);
                     }
                     // mark dirty handled by comparison against originalBalanceData
                    }}
                    placeholder={currentYearField.placeholder}
                    aria-describedby={`${currentYearFieldName}-helper`}
                    aria-invalid={false}
                  />
                  {!canEditCurrent || isSaving ? (
                    <button 
                      type="button" 
                      aria-label={`${currentYearField.helperText} is locked`} 
                      disabled 
                      className="date-input-icon"
                    >
                      <img src={lockIcon} alt="locked" />
                    </button>
                  ) : null}
                {fieldErrors[currentYearFieldName] && showValidation && (
                  <div className="text-red-700 text-sm mt-2">{fieldErrors[currentYearFieldName]}</div>
                )}
                </div>
              </div>
            </div>
            {/* Prior year field */}
            <div>
              <div className="helper-text font-semibold text-sm" id={`${priorYearFieldName}-helper`}>
                {priorYearField.helperText}
              </div>
              <div className="relative">
                <div className="input-with-icon">
                  <input
                    autoComplete="on"
                    className={`w-[547px]! h-12 shrink-0 block ds-form__control ${
                      canEditPrior && !isSaving ? '' : 'ds-form__disabled-field bg-gray-100 text-gray-600 pr-12'
                    }`}
                    disabled={!canEditPrior || isSaving}
                    id={priorYearFieldName}
                    name={priorYearFieldName}
                    type="text"
                    value={balanceData[priorYearFieldName] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                     setBalanceData(prev => ({ ...prev, [priorYearFieldName]: val }));
                     setFieldErrors(prev => ({ ...prev, [priorYearFieldName]: '' }));
                     setShowValidation(false);
                     // mark dirty handled by comparison against originalBalanceData
                    }}
                    placeholder={priorYearField.placeholder}
                    aria-describedby={`${priorYearFieldName}-helper`}
                    aria-invalid={false}
                  />
                  {!canEditPrior || isSaving ? (
                    <button 
                      type="button" 
                      aria-label={`${priorYearField.helperText} is locked`} 
                      disabled 
                      className="date-input-icon"
                    >
                      <img src={lockIcon} alt="locked" />
                    </button>
                  ) : null}
                {fieldErrors[priorYearFieldName] && showValidation && (
                  <div className="text-red-700 text-sm mt-2">{fieldErrors[priorYearFieldName]}</div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const standardHoursField = balanceFields.find(f => f.name === 'standardHours');

  // Show Edit only when either current or prior year balance id exists (> 0)
  const showEditButton = Boolean(
    balances && (Number(balances.cyEmplBalId) > 0 || Number(balances.pyEmplBalId) > 0)
  );

  // Track eligible override fields and whether they were edited
  const eligibleFields = ['currentYearEligibleOverride', 'priorYearEligibleOverride'];
  const isAnyEligibleEdited = eligibleFields.some(
    (field) => balanceData[field] !== originalBalanceData[field]
  );

  const handleEdit = () => {
    setFieldErrors({});
    setShowValidation(false);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setBalanceData(originalBalanceData);
    setFieldErrors({});
    setShowValidation(false);
    setIsEditMode(false);
  };

  const handleSave = async () => {
    if (!applicationId) return;
    if (!isAnyEligibleEdited) return; // nothing to save

    const nuid = safeGet(applicationData?.employeeNuId, '');
    const validationMsg = 'Current Reimbursable Amount cannot be greater than Eligible Amount Override';

    const buildPayload = () => {
      const payload: any = {};
      const cyEdited = (balanceData['currentYearEligibleOverride'] || '') !== (originalBalanceData['currentYearEligibleOverride'] || '');
      const pyEdited = (balanceData['priorYearEligibleOverride'] || '') !== (originalBalanceData['priorYearEligibleOverride'] || '');

      if (cyEdited && Number(balances?.cyEmplBalId) > 0) {
        payload.cyBalEmplBalId = String(balances.cyEmplBalId);
        const val = balanceData['currentYearEligibleOverride'];
        payload.cyEligAmntOverride = val !== undefined && val !== '' ? Number(val) : null;
      }

      if (pyEdited && Number(balances?.pyEmplBalId) > 0) {
        payload.pyBalEmplBalId = String(balances.pyEmplBalId);
        const val = balanceData['priorYearEligibleOverride'];
        payload.pyEligAmntOverride = val !== undefined && val !== '' ? Number(val) : null;
      }

      return payload;
    };

    const validate = () => {
      const currReimb = parseBalanceNumber(balances?.cyReimbursableBalance);
      const currOverride = parseBalanceNumber(balanceData['currentYearEligibleOverride']);
      const priorReimb = parseBalanceNumber(balances?.pyReimbursableBalance);
      const priorOverride = parseBalanceNumber(balanceData['priorYearEligibleOverride']);
      const hasCurrOverride = balanceData['currentYearEligibleOverride'] !== undefined && balanceData['currentYearEligibleOverride'] !== '';
      const hasPriorOverride = balanceData['priorYearEligibleOverride'] !== undefined && balanceData['priorYearEligibleOverride'] !== '';
      const currCap = getOverrideCap();
      setFieldErrors({});

      const currFail = hasCurrOverride && currReimb > currOverride;
      const priorFail = hasPriorOverride && priorReimb > priorOverride;
      const currCapFail = hasCurrOverride && currCap !== null && currOverride > currCap;

      if (currFail || priorFail || currCapFail) {
        setEligibilityErrors(currFail, priorFail, currCapFail, currCap ?? undefined);
        return false;
      }
      return true;
    };

    const handleApiResult = (resultAction: any) => {
      if (resultAction?.meta?.requestStatus === 'fulfilled') {
        (dispatch as any)(getBalanceInfo({ applicationId: safeGet(applicationId, ''), nuid }));
        const successMsg = safeGet(resultAction?.payload?.message, 'Balances updated successfully');
        setNotificationMessage(String(successMsg));
        setShowNotification(true);
        setShowPatchErrorBanner(false);
        setTimeout(() => setShowNotification(false), 5000);
        setIsEditMode(false);
        return;
      }

      // For rejected cases, check if it's a validation error
      const apiMessage = resultAction?.payload || resultAction?.error?.message || 'Failed to edit balances';
      if (String(apiMessage).includes(validationMsg)) {
        setEligibilityErrors(true, true, false);
      }
      // Note: balancesPatchError from redux will show the notification banner at the top
    };

    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = buildPayload();
      const resultAction: any = await (dispatch as any)(editBalanceDetails({ applicationId, data: payload, nuid }));
      handleApiResult(resultAction);
    } catch (err) {
      const msg = (err && (err as any).message) ? (err as any).message : 'Failed to edit balances';
      if (String(msg).includes(validationMsg)) {
        setEligibilityErrors(true, true, false);
      } else {
        setNotificationMessage(String(msg));
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 5000);
      }
    } finally {
      setIsSaving(false);
    }
  };
    // If GET returned an error, replace the whole UI with a centered error panel
  if (balancesError && !balancesLoading) {
    return (
      <div className="margin-top-4u flex items-center justify-center min-h-[300px]">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4">
              <img src={ErrorIcon} alt="error icon" className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="h3 text-red-600 mb-2">Error</h3>
            <p className="text-gray-700 text-sm">{balancesError}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="balances-tab flex flex-col gap-[27px]">
      {showNotification && (
        <Notifications messageInfo={notificationMessage} status={true} />
      )}
      {balancesPatchError && showPatchErrorBanner && (
        <Notifications messageInfo={balancesPatchError} status={false} />
      )}
      <TuitionErrorBanner currentStep={0} errors={showValidation ? fieldErrors : {}} onFieldClick={(f) => {
        const el = document.getElementById(f) || document.querySelector(`[name="${f}"]`);
        if (el) el.focus();
      }} formData={{}} />
      <div>
        {showEditButton && !isEditMode && (
          <button 
            type="button"
            onClick={handleEdit}
            className="button -primary -light p-3 flex items-center justify-center"
            disabled={isSaving}
            aria-busy={isSaving}
          >
            Edit
          </button>
        )}
        {isEditMode && (
          <div className="flex gap-4">
            <button 
              type="button"
              onClick={handleSave}
              className="button -primary -light p-3 flex items-center justify-center"
              disabled={isSaving || !isAnyEligibleEdited}
              aria-busy={isSaving}
            >
              Save
            </button>
            <button 
              type="button"
              onClick={handleCancel}
              className="button -secondary -light p-3 flex items-center justify-center"
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        )}
        {isSaving && <Loader />}
      </div>
      {balancesError && (
        <div className="p-4 mb-4 text-red-700 bg-red-100 border border-red-400 rounded">
          {balancesError}
        </div>
      )}
      
      <div>
        <h2 className="h3 mb-0">Employee Balance Information</h2>
      </div>

      {balancesLoading ? (
        <Loader />
      ) : (
        <>
      {/* Standard hours - full width */}
      {standardHoursField && (
        <div className="p-4!">
          <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input" data-ds-version="1">
            <div className="flex items-center gap-[37px]">
              <div>
                <label htmlFor={standardHoursField.name} className="label-ds2 text-[18px] font-[350] mb-0 !w-[210px] flex-shrink-0 text-right">
                  {standardHoursField.label}
                </label>
              </div>
              <div>
                {standardHoursField.helperText && (
                  <div className="helper-text font-semibold text-sm" id={`${standardHoursField.name}-helper`}>
                    {standardHoursField.helperText}
                  </div>
                )}
                <div className="relative">
                  <div className="input-with-icon">
                    <input
                      autoComplete="on"
                      className="!w-[547px] h-[48px] flex-shrink-0 block ds-form__control ds-form__disabled-field bg-gray-100 text-gray-600 pr-12"
                      disabled={true}
                      id={standardHoursField.name}
                      name={standardHoursField.name}
                      type="text"
                      value={balanceData[standardHoursField.name] || ''}
                      placeholder={standardHoursField.placeholder}
                      aria-describedby={`${standardHoursField.name}-helper`}
                      aria-invalid={false}
                    />
                    <button 
                      type="button" 
                      aria-label={`${standardHoursField.helperText} is locked`} 
                      disabled 
                      className="date-input-icon"
                    >
                      <img src={lockIcon} alt="locked" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Config-driven balance groups */}
      {balanceGroupsConfig.map((group) => (
        <div key={group.id}>
          {group.id === 'eligibleOverride' ? (
            renderGroupedFields(
              group.label,
              group.currentField,
              group.priorField,
              Boolean(isEditMode && Number(balances?.cyEmplBalId) > 0),
              Boolean(isEditMode && Number(balances?.pyEmplBalId) > 0)
            )
          ) : (
            renderGroupedFields(
              group.label,
              group.currentField,
              group.priorField,
              group.editable,
              group.editable
            )
          )}
        </div>
      ))}

      {/* Divider */}
      <div className="border-b border-[#B3B6C5]"></div>

      {/* Remaining balance section */}
      {balanceRemainingConfig.map((group) => (
        <div key={group.id}>
          {renderGroupedFields(group.label, group.currentField, group.priorField, group.editable, group.editable)}
        </div>
      ))}

      {/* Explanatory text */}
      <div>
        <p className="text-[18px] font-[350] leading-[24px]">
          Estimated remaining eligible amount is the net of eligible amount or eligible amount override (if populated), outstanding estimated fees, and reimbursable fees.
        </p>
      </div>
        </>
      )}
    </div>
  );
};

export default Balances;